import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Autocomplete,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useLoader } from '../../contexts/LoaderContext';
import { useModal } from '../../contexts/ModalContext';

const AssignmentRules = () => {
  const { showLoader, hideLoader } = useLoader();
  const { openSuccessModal, openErrorModal } = useModal();
  
  const [rules, setRules] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, rule: null });
  const [showAdvancedConditions, setShowAdvancedConditions] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    priority: 50, // Default to Medium
    is_active: true,
    assignment_strategy: 'round_robin',
    conditions: {
      source: [],
      lcp: [],
      urgency: [],
      tags: [],
      budget_min: '',
      budget_max: '',
      location_preferences: [],
    },
    agent_ids: [],
  });

  // Options for dropdowns
  const sourceOptions = [
    'manual', 'website', 'facebook', 'instagram', 'google', 
    'magicbricks', '99acres', 'housing.com', 'referral', 'walk-in'
  ];
  
  const lcpOptions = ['hot', 'warm', 'cold'];
  const urgencyOptions = ['high', 'medium', 'low'];
  
  const strategyOptions = [
    { value: 'round_robin', label: 'Round Robin', description: 'Distribute leads equally in rotation' },
  ];

  // Priority options
  const priorityOptions = [
    { value: 100, label: 'Highest Priority', description: 'Evaluated first - for most important leads' },
    { value: 75, label: 'High Priority', description: 'Evaluated second - for important leads' },
    { value: 50, label: 'Medium Priority', description: 'Standard priority - for regular leads' },
    { value: 25, label: 'Low Priority', description: 'Lower priority - for less urgent leads' },
    { value: 0, label: 'Lowest Priority', description: 'Evaluated last - typically used as catch-all rule' },
  ];

  // Helper function to get priority label from value
  const getPriorityLabel = (priorityValue) => {
    const option = priorityOptions.find(opt => opt.value === priorityValue);
    return option ? option.label : `Priority ${priorityValue}`;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchRules(),
        fetchAgents(),
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      openErrorModal('Failed to load assignment rules data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRules = async () => {
    try {
      const response = await api.get('/lms/assignment-rules/');
      if (response.data.success) {
        setRules(response.data.rules || []);
      }
    } catch (error) {
      console.error('Error fetching rules:', error);
      throw error;
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await api.get('/lms/agent_list/');
      // Response.data is already the array of active agents
      setAgents(response.data || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
      // Don't throw - agents is optional
    }
  };


  const handleOpenRuleModal = (rule = null) => {
    if (rule) {
      // Editing existing rule
      setEditingRule(rule);
      setFormData({
        name: rule.name,
        priority: rule.priority,
        is_active: rule.is_active,
        assignment_strategy: rule.assignment_strategy,
        conditions: rule.conditions || {
          source: [],
          lcp: [],
          urgency: [],
          tags: [],
          budget_min: '',
          budget_max: '',
          location_preferences: [],
        },
        agent_ids: rule.agents_data?.map(a => a.agent_id || a.id) || [],
      });
    } else {
      // Creating new rule
      setEditingRule(null);
      setFormData({
        name: '',
        priority: 50, // Default to Medium Priority
        is_active: true,
        assignment_strategy: 'round_robin',
        conditions: {
          source: [],
          lcp: [],
          urgency: [],
          tags: [],
          budget_min: '',
          budget_max: '',
          location_preferences: [],
        },
        agent_ids: [],
      });
    }
    setShowRuleModal(true);
  };

  const handleCloseRuleModal = () => {
    setShowRuleModal(false);
    setEditingRule(null);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleConditionChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      conditions: {
        ...prev.conditions,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      openErrorModal('Please enter a rule name');
      return;
    }
    if (formData.agent_ids.length === 0) {
      openErrorModal('Please select at least one agent');
      return;
    }

    // Clean up conditions - remove empty values
    const cleanedConditions = {};
    Object.keys(formData.conditions).forEach(key => {
      const value = formData.conditions[key];
      if (Array.isArray(value) && value.length > 0) {
        cleanedConditions[key] = value;
      } else if (!Array.isArray(value) && value !== '' && value !== null) {
        cleanedConditions[key] = value;
      }
    });

    const payload = {
      ...formData,
      conditions: cleanedConditions,
    };

    showLoader();
    try {
      let response;
      if (editingRule) {
        response = await api.put(`/lms/assignment-rules/${editingRule.id}/`, payload);
      } else {
        response = await api.post('/lms/assignment-rules/', payload);
      }

      if (response.data.success) {
        openSuccessModal(
          editingRule 
            ? 'Assignment rule updated successfully' 
            : 'Assignment rule created successfully'
        );
        handleCloseRuleModal();
        fetchRules();
      } else {
        openErrorModal(response.data.error || 'Failed to save assignment rule');
      }
    } catch (error) {
      console.error('Error saving rule:', error);
      openErrorModal(
        error.response?.data?.errors 
          ? JSON.stringify(error.response.data.errors)
          : 'Failed to save assignment rule'
      );
    } finally {
      hideLoader();
    }
  };

  const handleToggleRule = async (rule) => {
    showLoader();
    try {
      const response = await api.post(`/lms/assignment-rules/${rule.id}/toggle/`);
      if (response.data.success) {
        openSuccessModal(response.data.message);
        fetchRules();
      }
    } catch (error) {
      console.error('Error toggling rule:', error);
      openErrorModal('Failed to toggle assignment rule');
    } finally {
      hideLoader();
    }
  };

  const handleDeleteRule = async (rule) => {
    setDeleteConfirmDialog({ open: true, rule });
  };

  const confirmDelete = async () => {
    const rule = deleteConfirmDialog.rule;
    setDeleteConfirmDialog({ open: false, rule: null });
    
    showLoader();
    try {
      const response = await api.delete(`/lms/assignment-rules/${rule.id}/`);
      if (response.data.success) {
        openSuccessModal(response.data.message);
        fetchRules();
      }
    } catch (error) {
      console.error('Error deleting rule:', error);
      openErrorModal('Failed to delete assignment rule');
    } finally {
      hideLoader();
    }
  };

  const getStrategyLabel = (strategyValue) => {
    const strategy = strategyOptions.find(s => s.value === strategyValue);
    return strategy ? strategy.label : strategyValue;
  };

  const getConditionsSummary = (conditions) => {
    const parts = [];
    if (conditions.source?.length > 0) {
      parts.push(`Source: ${conditions.source.join(', ')}`);
    }
    if (conditions.lcp?.length > 0) {
      parts.push(`LCP: ${conditions.lcp.join(', ')}`);
    }
    if (conditions.urgency?.length > 0) {
      parts.push(`Urgency: ${conditions.urgency.join(', ')}`);
    }
    if (conditions.budget_min || conditions.budget_max) {
      const budgetStr = `Budget: ${conditions.budget_min || '0'} - ${conditions.budget_max || '∞'}`;
      parts.push(budgetStr);
    }
    if (conditions.tags?.length > 0) {
      parts.push(`Tags: ${conditions.tags.join(', ')}`);
    }
    if (conditions.location_preferences?.length > 0) {
      parts.push(`Locations: ${conditions.location_preferences.join(', ')}`);
    }
    
    return parts.length > 0 ? parts.join(' | ') : 'No conditions (matches all leads)';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Lead Assignment Rules
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Automate lead distribution with round-robin and rule-based assignment
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenRuleModal()}
        >
          Create Rule
        </Button>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        Rules are evaluated by priority (highest first). The first matching rule assigns the lead. 
        Agents will receive leads in rotation based on the selected strategy.
      </Alert>

      {/* Rules List */}
      {rules.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Assignment Rules Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first rule to start automating lead distribution
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenRuleModal()}
            >
              Create First Rule
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {rules.map((rule) => (
            <Grid item xs={12} key={rule.id}>
              <Card 
                sx={{ 
                  border: rule.is_active ? '2px solid' : '1px solid',
                  borderColor: rule.is_active ? 'primary.main' : 'divider',
                  opacity: rule.is_active ? 1 : 0.7,
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6">
                          {rule.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={getPriorityLabel(rule.priority)}
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          icon={rule.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                          label={rule.is_active ? 'Active' : 'Inactive'}
                          color={rule.is_active ? 'success' : 'default'}
                        />
                        <Chip
                          size="small"
                          label="Round Robin"
                          color="secondary"
                          variant="outlined"
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {getConditionsSummary(rule.conditions)}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
                        <Tooltip title="Agents in this rule">
                          <Chip
                            icon={<GroupIcon />}
                            label={`${rule.active_agents_count} agents`}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                        <Tooltip title="Total leads assigned">
                          <Chip
                            label={`${rule.total_leads_assigned} leads assigned`}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={rule.is_active ? 'Deactivate' : 'Activate'}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleRule(rule)}
                          color={rule.is_active ? 'success' : 'default'}
                        >
                          {rule.is_active ? <ToggleOnIcon /> : <ToggleOffIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit rule">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenRuleModal(rule)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete rule">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteRule(rule)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Agents List */}
                  {rule.agents_data && rule.agents_data.length > 0 && (
                    <Accordion sx={{ mt: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2">
                          View Agents ({rule.agents_data.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {rule.agents_data.map((agent) => {
                            const agentId = agent.agent_id || agent.id;
                            const agentName = agent.agent_name || agent.name || 'Unknown';
                            const teamName = agent.team_name || 'No Team';
                            const designation = agent.designation || null;
                            
                            return (
                              <Box 
                                key={agentId}
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 1,
                                  p: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  bgcolor: agent.is_active ? 'background.paper' : 'action.disabledBackground'
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {agentName}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {teamName}
                                    </Typography>
                                    {designation && (
                                      <>
                                        <Typography variant="caption" color="text.secondary">•</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {designation}
                                        </Typography>
                                      </>
                                    )}
                                  </Box>
                                </Box>
                                {!agent.is_active && (
                                  <Chip 
                                    label="Inactive" 
                                    size="small" 
                                    color="default" 
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Rule Modal */}
      <Dialog
        open={showRuleModal}
        onClose={handleCloseRuleModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingRule ? 'Edit Assignment Rule' : 'Create Assignment Rule'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Step 1: Rule Name & Priority */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Rule Name *"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                placeholder="e.g., Website Leads to Sales Team"
                required
                sx={{ mb: 2 }}
              />
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Priority Level</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => handleFormChange('priority', e.target.value)}
                  label="Priority Level"
                >
                  {priorityOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box>
                        <Typography variant="body1">{option.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleFormChange('is_active', e.target.checked)}
                  />
                }
                label="Activate rule immediately"
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Step 2: Select Agents - Most Important */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Select Agents *
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose which agents will receive leads from this rule (distributed equally in rotation)
              </Typography>
              
              <Autocomplete
                multiple
                options={agents}
                getOptionLabel={(option) => option.agent_name || option.name || option.email || 'Unknown'}
                value={agents.filter(a => formData.agent_ids.includes(a.agent_id || a.id))}
                onChange={(e, value) => handleFormChange('agent_ids', value.map(v => v.agent_id || v.id))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Agents"
                    placeholder="Select agents"
                    error={formData.agent_ids.length === 0}
                    helperText={formData.agent_ids.length === 0 ? 'At least one agent is required' : `${formData.agent_ids.length} agent(s) selected`}
                  />
                )}
                renderOption={(props, option) => {
                  const name = option.agent_name || option.name || 'Unknown';
                  const team = option.team_name || 'No Team';
                  const designation = option.designation || null;
                  
                  return (
                    <li {...props}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.secondary">
                            {team}
                          </Typography>
                          {designation && (
                            <>
                              <Typography variant="caption" color="text.secondary">•</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {designation}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                    </li>
                  );
                }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Step 3: Filters (Optional, Collapsible) */}
            <Accordion 
              expanded={showAdvancedConditions}
              onChange={() => setShowAdvancedConditions(!showAdvancedConditions)}
              sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  <Typography variant="h6">
                    Lead Filters (Optional)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Leave empty to assign all leads to selected agents
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={sourceOptions}
                      value={formData.conditions.source || []}
                      onChange={(e, value) => handleConditionChange('source', value)}
                      renderInput={(params) => (
                        <TextField {...params} label="Source" placeholder="e.g., website, facebook" size="small" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      multiple
                      options={lcpOptions}
                      value={formData.conditions.lcp || []}
                      onChange={(e, value) => handleConditionChange('lcp', value)}
                      renderInput={(params) => (
                        <TextField {...params} label="Lead Quality" placeholder="hot, warm, cold" size="small" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      multiple
                      options={urgencyOptions}
                      value={formData.conditions.urgency || []}
                      onChange={(e, value) => handleConditionChange('urgency', value)}
                      renderInput={(params) => (
                        <TextField {...params} label="Urgency" placeholder="high, medium, low" size="small" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Min Budget"
                      value={formData.conditions.budget_min || ''}
                      onChange={(e) => handleConditionChange('budget_min', e.target.value)}
                      placeholder="Optional"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Max Budget"
                      value={formData.conditions.budget_max || ''}
                      onChange={(e) => handleConditionChange('budget_max', e.target.value)}
                      placeholder="Optional"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={['Mumbai', 'Pune', 'Bangalore', 'Delhi', 'Gurgaon', 'Noida']}
                      value={formData.conditions.location_preferences || []}
                      onChange={(e, value) => handleConditionChange('location_preferences', value)}
                      renderInput={(params) => (
                        <TextField {...params} label="Location Preferences" placeholder="Optional" size="small" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={formData.conditions.tags || []}
                      onChange={(e, value) => handleConditionChange('tags', value)}
                      renderInput={(params) => (
                        <TextField {...params} label="Tags" placeholder="Type and press enter" size="small" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="info" icon={<InfoIcon />}>
                      <Typography variant="caption">
                        All filters use AND logic. Only leads matching ALL selected filters will be assigned to the selected agents.
                      </Typography>
                    </Alert>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.default' }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            * Required fields
          </Typography>
          <Button onClick={handleCloseRuleModal} size="large">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            size="large"
            disabled={!formData.name.trim() || formData.agent_ids.length === 0}
          >
            {editingRule ? 'Update Rule' : 'Create Rule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, rule: null })}
      >
        <DialogTitle>Delete Assignment Rule?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the rule "<strong>{deleteConfirmDialog.rule?.name}</strong>"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. Historical assignment logs will be preserved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog({ open: false, rule: null })}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssignmentRules;
